import React, { useState, useEffect } from 'react';
import { Link as RouterLink, useLocation } from 'react-router-dom';
import { Container, Typography, Button, Paper, Box, Alert, CircularProgress } from '@mui/material';
import AuthService from '../services/auth.service';

const UserApprovalPage: React.FC = () => {
  const location = useLocation();
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // Extract token and email from URL query parameters
  useEffect(() => {
    const queryParams = new URLSearchParams(location.search);
    const token = queryParams.get('token');
    const email = queryParams.get('email');
    
    if (!token || !email) {
      setError('Invalid or missing approval parameters.');
      setLoading(false);
    } else {
      approveUser(email, token);
    }
  }, [location]);

  const approveUser = async (email: string, token: string) => {
    try {
      const response = await AuthService.approveUser(email, token);
      setSuccess(response.message || 'User has been approved successfully!');
    } catch (err: any) {
      if (err.response && err.response.data) {
        setError(err.response.data.detail || 'User approval failed. Please try again.');
      } else {
        setError('An error occurred. Please try again later.');
      }
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Container maxWidth="sm">
        <Paper elevation={3} sx={{ p: 4, mt: 8, textAlign: 'center' }}>
          <CircularProgress sx={{ mb: 2 }} />
          <Typography variant="h6">
            Processing user approval...
          </Typography>
        </Paper>
      </Container>
    );
  }

  return (
    <Container maxWidth="sm">
      <Paper elevation={3} sx={{ p: 4, mt: 8 }}>
        <Typography variant="h4" component="h1" align="center" gutterBottom>
          User Approval
        </Typography>
        
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        
        {success && (
          <Alert severity="success" sx={{ mb: 2 }}>
            {success}
          </Alert>
        )}
        
        <Typography variant="body1" paragraph align="center">
          {success 
            ? 'The user has been approved and will now be able to access the system. An email notification has been sent to inform them.'
            : 'There was a problem approving the user. Please try again or contact support.'}
        </Typography>
        
        <Box sx={{ mt: 3, textAlign: 'center' }}>
          <Button
            variant="contained"
            color="primary"
            component={RouterLink}
            to="/login"
            sx={{ mx: 1 }}
          >
            Go to Login
          </Button>
        </Box>
      </Paper>
    </Container>
  );
};

export default UserApprovalPage;
